import { ConfigProvider, theme as antTheme } from "antd";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_ACCENT, DEFAULT_THEME, THEMES, resolveTokens } from "./tokens";

const STORAGE_KEY = "undr.theme";
const UI_KEY = "undr.ui";

const ThemeContext = createContext(null);

function readJSON(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...JSON.parse(raw) };
  } catch (e) {
    return fallback;
  }
}

function writeJSON(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* storage unavailable */
  }
}

const DEFAULT_PREFS = { theme: DEFAULT_THEME, accent: DEFAULT_ACCENT, followSystem: false };
const DEFAULT_UI = { colorRows: true };

function systemPrefersDark() {
  return typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }) {
  const [prefs, setPrefs] = useState(() => readJSON(STORAGE_KEY, DEFAULT_PREFS));
  const [ui, setUi] = useState(() => readJSON(UI_KEY, DEFAULT_UI));
  const [sysDark, setSysDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSysDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);

  // Effective theme: when following system, Dark if system is dark, otherwise the chosen light theme
  // (or Cream if the chosen theme is Dark).
  const effectiveTheme = useMemo(() => {
    if (!prefs.followSystem) return THEMES[prefs.theme] ? prefs.theme : DEFAULT_THEME;
    if (sysDark) return "Dark";
    return prefs.theme === "Dark" ? "Cream" : prefs.theme;
  }, [prefs, sysDark]);

  const tokens = useMemo(() => resolveTokens(effectiveTheme, prefs.accent), [effectiveTheme, prefs.accent]);
  const isDark = effectiveTheme === "Dark";

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(tokens).forEach(([k, v]) => root.style.setProperty(`--${k}`, v));
    root.setAttribute("data-theme", isDark ? "dark" : "light");
    root.style.colorScheme = isDark ? "dark" : "light";
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", tokens.sidebar);
  }, [tokens, isDark]);

  useEffect(() => writeJSON(STORAGE_KEY, prefs), [prefs]);
  useEffect(() => writeJSON(UI_KEY, ui), [ui]);

  const setTheme = useCallback((theme) => setPrefs((p) => ({ ...p, theme })), []);
  const setAccent = useCallback((accent) => setPrefs((p) => ({ ...p, accent })), []);
  const setFollowSystem = useCallback((followSystem) => setPrefs((p) => ({ ...p, followSystem })), []);
  const setColorRows = useCallback((colorRows) => setUi((u) => ({ ...u, colorRows })), []);

  const antConfig = useMemo(
    () => ({
      algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
      token: {
        colorPrimary: tokens.accent,
        colorBgLayout: tokens.bg,
        colorBgContainer: tokens.surface,
        colorBgElevated: tokens.surface,
        colorBorder: tokens.border,
        colorBorderSecondary: tokens.border,
        colorText: tokens.ink,
        colorTextSecondary: tokens.muted,
        borderRadius: 8,
        fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
        fontSize: 13,
      },
    }),
    [tokens, isDark]
  );

  const value = useMemo(
    () => ({
      theme: prefs.theme,
      effectiveTheme,
      accent: prefs.accent,
      followSystem: prefs.followSystem,
      isDark,
      tokens,
      colorRows: ui.colorRows,
      setTheme,
      setAccent,
      setFollowSystem,
      setColorRows,
    }),
    [prefs, effectiveTheme, isDark, tokens, ui.colorRows, setTheme, setAccent, setFollowSystem, setColorRows]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
